import { getBearerTokenUrl, getProfileUrl } from './urls';
import { getSiteId } from 'lib/gql/getSiteId';
import { verifyCaptcha } from 'lib/services/captcha-service';
import { updateUserProfileWithFederation } from './registration-service';

interface GraphAuthTokensResponse {
  access_token?: string;
  token_type?: string;
  expires_in?: number;
  refresh_token?: string;
  error?: string;
  error_description?: string;
}

export interface Question {
  QuestionId: string;
  Answers: {
    AnswerId: string;
  }[];
}
export interface UserProfile {
  UnifiedId?: string;
  UserId?: string;
  Status?: string;

  Email?: string;
  Username?: string;
  Address1?: string;
  Address2?: string;
  Address3?: string;
  BirthDate?: string;
  BirthMonth?: number;
  BirthYear?: number;
  City?: string;
  FirstName?: string;
  Gender?: string;
  LastName?: string;
  MiddleName?: string;
  PostalCode?: string;
  StateOrProvince?: string;
  Salutation?: string;
  CountryCode?: string;
  CountryName?: string;
  Phone?: string;
  PhoneType?: string;
  Subscriptions?: [];
  Questions?: Question[];

  CultureName?: string;
  RegisterDate?: string;
  HasPassword?: boolean;
  TermsOfUseAcceptance?: string;

  error?: string;
  error_description?: string;
}

export interface CredentialsParams {
  username: string;
  password: string;
}
export interface OAuthParams {
  provider: string;
  userToken: string;
}
export interface GetGraphAuthTokensParams {
  credentials?: CredentialsParams;
  oauth?: OAuthParams;
  client_credentials?: boolean;
  siteId?: string;
  failTrigger?: boolean;
}
export async function getGraphAuthTokens({
  credentials,
  oauth,
  client_credentials,
  siteId,
  failTrigger,
}: GetGraphAuthTokensParams): Promise<GraphAuthTokensResponse | null> {
  try {
    const resolvedSiteId = siteId || process.env.GMI_GRAPH_SITE_ID;
    if (!resolvedSiteId) {
      console.error('Site ID is required to get Graph Bearer Token');
      return null;
    }

    const bearerTokenUrl = getBearerTokenUrl(resolvedSiteId);

    const gmiGraphClientId = process.env.GMI_GRAPH_CLIENT_ID;
    const gmiGraphClientSecret = process.env.GMI_GRAPH_CLIENT_SECRET;

    const gmiGraphScope =
      'username email birthdate address location name subscriptions public language status retailer questions federation gender phone';

    let requestBody: Record<string, string>;
    if (credentials) {
      requestBody = {
        grant_type: 'password',
        client_id: gmiGraphClientId ?? '',
        client_secret: gmiGraphClientSecret ?? '',
        username: credentials.username,
        password: credentials.password,
        scope: gmiGraphScope,
      };
    } else if (oauth) {
      requestBody = {
        grant_type: 'gmi_federation',
        client_id: gmiGraphClientId ?? '',
        client_secret: gmiGraphClientSecret ?? '',
        gmi_federation_provider: oauth.provider,
        gmi_federation_provider_user_token: oauth.userToken,
        scope: gmiGraphScope,
      };
    } else if (client_credentials) {
      requestBody = {
        grant_type: 'client_credentials',
        client_id: gmiGraphClientId ?? '',
        client_secret: gmiGraphClientSecret ?? '',
      };
    } else {
      throw new Error('No credentials or oauth provided');
    }
    const bearerResponse = await fetch(bearerTokenUrl, {
      method: 'POST',
      body: new URLSearchParams(requestBody).toString(),
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        Accept: 'application/json',
      },
    });

    const bearerResponseJson = (await bearerResponse.json()) as GraphAuthTokensResponse;

    if (bearerResponseJson?.error) {
      let errorCode = gmiApiResponseErrorCodes.find((code) =>
        bearerResponseJson?.error_description?.toLowerCase()?.includes(code.message.toLowerCase())
      )?.code;
      if (!errorCode) {
        errorCode = 'GENERIC_ERROR';
      }
      const errorLogMessage = `Graph API error: ${bearerResponseJson.error}. Unable to obtain auth tokens.`;
      console.info(errorLogMessage);
      return {
        error: errorCode,
      };
    }

    return bearerResponseJson;
  } catch (error) {
    console.error(`Graph Bearer token error: ${error}`);
    if (failTrigger) {
      throw new Error(`Graph Bearer token error: ${error}`);
    }
  }
  return {
    error: 'GENERIC_ERROR',
  };
}

export async function getUserProfileDetails(
  accessToken: string,
  failTrigger = true
): Promise<UserProfile | null> {
  try {
    let profileUrl = getProfileUrl();
    profileUrl = `${profileUrl}/users/me`;
    const response = await fetch(profileUrl, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      console.error(`Error fetching user profile: ${response.statusText}`);
      if (failTrigger) {
        throw new Error(`Error fetching user profile: ${response.statusText}`);
      }
    }

    const userProfileDetails = (await response.json()) as UserProfile;
    if (userProfileDetails.error) {
      let errorCode = gmiApiResponseErrorCodes.find((code) =>
        userProfileDetails?.error_description?.toLowerCase()?.includes(code.message.toLowerCase())
      )?.code;
      if (!errorCode) {
        errorCode = 'GENERIC_ERROR';
      }
      const errorLogMessage = `Error fetching user profile: ${userProfileDetails.error}.`;
      console.info(errorLogMessage);
      return {
        error: errorCode,
      };
    }

    if (!userProfileDetails.UserId) {
      console.info(`Error fetching user profile: User ID not found`);
      return null;
    }

    return userProfileDetails;
  } catch (error) {
    console.error(`Error fetching user profile: ${error}`);
    if (failTrigger) {
      throw new Error(`Error fetching user profile: ${error}`);
    }
  }
  return {
    error: 'GENERIC_ERROR',
  };
}

const gmiApiResponseErrorCodes = [
  {
    code: 'MAX_ATTEMPTS_EXCEEDED',
    message: 'Maximum login attempts exceeded',
  },
  {
    code: 'ACCOUNT_LOCKED',
    message: 'Account locked',
  },
];

export interface CredentialsLoginRequest {
  type: 'credentials';
  credentials: CredentialsParams;
  captchaToken?: string;
  /**
   * For testing purposes, skip captcha verification if true
   */
  skipCaptcha?: boolean;
}

export interface OAuthLoginRequest {
  type: 'oauth';
  oauth: OAuthParams;
}

export interface ClientCredentialsLoginRequest {
  type: 'client_credentials';
}

/**
 * Social connect login request - for linking social account to existing user
 */
export interface SocialConnectLoginRequest {
  type: 'social_connect';
  credentials: CredentialsParams;
  federation: {
    provider: string;
    providerUserToken: string;
  };
  skipCaptcha?: boolean;
}

export type LoginRequest =
  | CredentialsLoginRequest
  | OAuthLoginRequest
  | ClientCredentialsLoginRequest
  | SocialConnectLoginRequest;

type LoginResult =
  | 'success'
  | 'RESET_PASSWORD_REQUIRED'
  | 'INVALID_CAPTCHA'
  | 'MISSING_CREDENTIALS'
  | 'AUTHENTICATION_FAILED'
  | 'USER_PROFILE_ERROR'
  | 'FEDERATION_UPDATE_FAILED'
  | 'GENERIC_ERROR';

export interface LoginWithValidationResponse {
  result?: LoginResult;
  errorDetails?: string;
  accessToken?: string;
  userProfileDetails?: UserProfile;
}

export async function loginWithValidation(
  loginRequest: LoginRequest,
  clientIp: string
): Promise<LoginWithValidationResponse> {
  try {
    if (loginRequest.type === 'credentials') {
      if (!loginRequest?.credentials) {
        return { result: 'MISSING_CREDENTIALS', errorDetails: 'Credentials are required.' };
      }
      if (!loginRequest?.credentials?.username || !loginRequest?.credentials?.password) {
        return {
          result: 'MISSING_CREDENTIALS',
          errorDetails: 'Username and password are required.',
        };
      }

      const isVerifyCaptcha =
        loginRequest.skipCaptcha !== true && Boolean(loginRequest.captchaToken);
      if (isVerifyCaptcha) {
        const isCaptchaValid = await verifyCaptcha(loginRequest.captchaToken as string, clientIp);
        if (!isCaptchaValid) {
          return { result: 'INVALID_CAPTCHA' };
        }
      }
    }
    if (loginRequest.type === 'oauth') {
      if (!loginRequest?.oauth) {
        return { result: 'MISSING_CREDENTIALS', errorDetails: 'OAuth are required.' };
      }
      if (!loginRequest?.oauth?.provider || !loginRequest?.oauth?.userToken) {
        return {
          result: 'MISSING_CREDENTIALS',
          errorDetails: 'Provider and user token are required.',
        };
      }
    }
    if (loginRequest.type === 'social_connect') {
      if (!loginRequest?.credentials) {
        return {
          result: 'MISSING_CREDENTIALS',
          errorDetails: 'Social connect: Credentials are required.',
        };
      }
      if (!loginRequest?.credentials?.username || !loginRequest?.credentials?.password) {
        return {
          result: 'MISSING_CREDENTIALS',
          errorDetails: 'Social connect: Username and password are required.',
        };
      }
      if (!loginRequest?.federation) {
        return { result: 'MISSING_CREDENTIALS', errorDetails: 'Federation data is required.' };
      }
    }

    const profileSiteId = await getSiteId();

    const requestParams: GetGraphAuthTokensParams = {
      siteId: profileSiteId,
      failTrigger: true,
    };
    if (loginRequest.type === 'credentials') {
      requestParams.credentials = loginRequest.credentials;
    }
    if (loginRequest.type === 'oauth') {
      requestParams.oauth = loginRequest.oauth;
    }
    if (loginRequest.type === 'client_credentials') {
      requestParams.client_credentials = true;
    }
    if (loginRequest.type === 'social_connect') {
      // For social connect, authenticate with credentials first
      requestParams.credentials = loginRequest.credentials;
    }
    const authResponse = await getGraphAuthTokens({
      ...requestParams,
    });
    if (!authResponse) {
      return { result: 'AUTHENTICATION_FAILED' };
    }

    if (authResponse.error && authResponse.error === 'GENERIC_ERROR') {
      return { result: 'AUTHENTICATION_FAILED', errorDetails: authResponse.error };
    }

    if (
      authResponse.error &&
      (authResponse.error === 'MAX_ATTEMPTS_EXCEEDED' || authResponse.error === 'ACCOUNT_LOCKED')
    ) {
      return { result: 'RESET_PASSWORD_REQUIRED', errorDetails: authResponse.error };
    }

    const accessTokenGenerated = authResponse.access_token;
    if (!accessTokenGenerated) {
      return { result: 'AUTHENTICATION_FAILED', errorDetails: 'Failed to generate access token' };
    }

    const userProfileDetails = await getUserProfileDetails(accessTokenGenerated);

    if (!userProfileDetails || userProfileDetails?.error) {
      return { result: 'USER_PROFILE_ERROR', errorDetails: userProfileDetails?.error };
    }

    // For social_connect, update user profile with federation data
    if (loginRequest.type === 'social_connect') {
      const federationUpdated = await updateUserProfileWithFederation(accessTokenGenerated, {
        Provider: loginRequest.federation.provider,
        ProviderUserToken: loginRequest.federation.providerUserToken,
      });

      if (!federationUpdated) {
        return {
          result: 'FEDERATION_UPDATE_FAILED',
          errorDetails: 'Failed to update user profile with federation data',
        };
      }
    }

    return {
      result: 'success',
      accessToken: accessTokenGenerated,
      userProfileDetails: userProfileDetails,
    };
  } catch (error) {
    console.error('Error during login request:', error);
    return {
      result: 'GENERIC_ERROR',
      errorDetails: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
}
